import type { RosterRow } from "@amber/application/contracts";
import type { StatusTone } from "@amber/ui";
import type { ProfilesSearch } from "./search";

export const FACETS = ["status", "person", "language", "platform", "problems", "stale", "owner"] as const;
export type Facet = (typeof FACETS)[number];

export const FACET_TITLES: Record<Facet, string> = {
  status: "Status",
  person: "Person",
  language: "Language",
  platform: "Platform",
  problems: "Has problems",
  stale: "Identity verification",
  owner: "Owner",
};

export const STATUS_FILTERS = [
  { key: "tracked", label: "Tracked" },
  { key: "pending", label: "Changes pending" },
  { key: "paused", label: "Paused" },
  { key: "draft", label: "Draft" },
  { key: "not_tracked", label: "Not tracked" },
  { key: "retired", label: "Retired" },
] as const;

const PROBLEM_FILTERS = [
  { key: "sources", label: "Source problems" },
  { key: "gaps", label: "Open gaps" },
  { key: "downloads", label: "Download problems" },
] as const;

const STALE_FILTERS = [
  { key: "stale", label: "Stale" },
  { key: "never", label: "Never verified" },
] as const;

export interface FacetOption {
  readonly key: string;
  readonly label: string;
}

function statusKey(row: RosterRow): string {
  if (row.lifecycle_state === "active") return "tracked";
  if (row.lifecycle_state === "paused") return "paused";
  if (row.lifecycle_state === "incidental") return "not_tracked";
  if (row.lifecycle_state === "archived") return "retired";
  return "draft";
}

function problemKeys(row: RosterRow): string[] {
  return [
    ...(row.source_problems > 0 ? ["sources"] : []),
    ...(row.open_gaps.count > 0 ? ["gaps"] : []),
    ...(row.download_problems.count > 0 ? ["downloads"] : []),
  ];
}

const FACET_VALUES: Record<Facet, (row: RosterRow) => readonly string[]> = {
  status: (row) => [statusKey(row), ...(row.plan?.pending ? ["pending"] : [])],
  person: (row) => [row.id],
  language: (row) => row.languages,
  platform: (row) => row.platforms.map((p) => p.key),
  problems: problemKeys,
  stale: (row) => (row.verification.state === "current" ? [] : [row.verification.state]),
  owner: (row) => [row.owner?.id ?? "none"],
};

const languageNames = new Intl.DisplayNames(["en-GB"], { type: "language" });

export function languageLabel(code: string): string {
  return languageNames.of(code) ?? code.toUpperCase();
}

const byLabel = (a: FacetOption, b: FacetOption) => a.label.localeCompare(b.label);

function distinct(people: readonly RosterRow[], pick: (row: RosterRow) => readonly FacetOption[]): FacetOption[] {
  const found = new Map<string, string>();
  for (const p of people) for (const o of pick(p)) found.set(o.key, o.label);
  return [...found].map(([key, label]) => ({ key, label })).sort(byLabel);
}

export function facetOptions(people: readonly RosterRow[]): Record<Facet, readonly FacetOption[]> {
  const owners = distinct(people, (p) => (p.owner ? [{ key: p.owner.id, label: p.owner.name }] : []));
  return {
    status: STATUS_FILTERS,
    person: distinct(people, (p) => [{ key: p.id, label: p.name }]),
    language: distinct(people, (p) => p.languages.map((l) => ({ key: l, label: languageLabel(l) }))),
    platform: distinct(people, (p) => p.platforms),
    problems: PROBLEM_FILTERS,
    stale: STALE_FILTERS,
    owner: [...owners, { key: "none", label: "No owner" }],
  };
}

const listOf = (value: string | undefined): string[] => (value ?? "").split(",").filter(Boolean);

export function toggled(search: ProfilesSearch, facet: Facet, key: string): ProfilesSearch {
  const current = listOf(search[facet]);
  const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
  return { ...search, [facet]: next.join(",") || undefined };
}

export interface RosterChip {
  readonly facet: Facet;
  readonly key: string;
  readonly label: string;
  readonly value: string;
}

export function rosterChips(search: ProfilesSearch, options: Record<Facet, readonly FacetOption[]>): RosterChip[] {
  return FACETS.flatMap((facet) =>
    listOf(search[facet]).map((key) => ({
      facet,
      key,
      label: FACET_TITLES[facet],
      value: options[facet].find((o) => o.key === key)?.label ?? key,
    })),
  );
}

export function toneOf(state: RosterRow["lifecycle_state"]): StatusTone {
  if (state === "active") return "running";
  if (state === "paused") return "waiting";
  if (state === "draft") return "invited";
  return "off";
}

const normalised = (value: string) => {
  try {
    return decodeURIComponent(value).toLowerCase();
  } catch {
    return value.toLowerCase();
  }
};

export function matches(row: RosterRow, q: string): string | null | false {
  const needle = normalised(q.trim());
  if (!needle) return null;
  if (row.name.toLowerCase().includes(needle) || (row.role ?? "").toLowerCase().includes(needle)) return null;
  const alias = row.aliases.find((a) => a.toLowerCase().includes(needle));
  if (alias) return `Alias: ${alias}`;
  if (row.wikidata_id?.toLowerCase() === needle) return `Wikidata: ${row.wikidata_id}`;
  const article = row.wikipedia.find((url) => normalised(url).includes(needle));
  if (article) return `Wikipedia: ${article}`;
  const account = row.accounts.find((url) => normalised(url).includes(needle));
  if (account) return `Account: ${account}`;
  return false;
}

type Selected = Record<Facet, ReadonlySet<string>>;

function perFacet<T>(make: (facet: Facet) => T): Record<Facet, T> {
  return {
    status: make("status"),
    person: make("person"),
    language: make("language"),
    platform: make("platform"),
    problems: make("problems"),
    stale: make("stale"),
    owner: make("owner"),
  };
}

function passes(row: RosterRow, selected: Selected, skip?: Facet): boolean {
  return FACETS.every((facet) => {
    const wanted = selected[facet];
    if (facet === skip || wanted.size === 0) return true;
    return FACET_VALUES[facet](row).some((v) => wanted.has(v));
  });
}

function facetCounts(people: readonly RosterRow[], selected: Selected): Record<Facet, Map<string, number>> {
  const counts = perFacet(() => new Map<string, number>());
  for (const facet of FACETS)
    for (const p of people) {
      if (!passes(p, selected, facet)) continue;
      for (const v of FACET_VALUES[facet](p)) counts[facet].set(v, (counts[facet].get(v) ?? 0) + 1);
    }
  return counts;
}

export function filterRoster(people: readonly RosterRow[], search: ProfilesSearch, showNotTracked: boolean) {
  const selected: Selected = perFacet((f) => new Set(listOf(search[f])));
  const searched = people.filter((p) => matches(p, search.q ?? "") !== false);
  const statusCounts = new Map<string, number>();
  for (const p of people) for (const k of FACET_VALUES.status(p)) statusCounts.set(k, (statusCounts.get(k) ?? 0) + 1);
  const filtered = searched.filter((p) => passes(p, selected));
  const statuses = selected.status;
  const hiddenNotTracked =
    statuses.size === 0 && !showNotTracked ? filtered.filter((p) => p.lifecycle_state === "incidental") : [];
  const hiddenRetired = statuses.has("retired") ? [] : filtered.filter((p) => p.lifecycle_state === "archived");
  return {
    selected,
    counts: facetCounts(searched, selected),
    statusCounts,
    monitored: filtered.filter((p) => p.lifecycle_state !== "incidental" && p.lifecycle_state !== "archived"),
    notTracked: filtered.filter((p) => p.lifecycle_state === "incidental" && !hiddenNotTracked.includes(p)),
    retired: filtered.filter((p) => p.lifecycle_state === "archived" && statuses.has("retired")),
    hiddenNotTracked,
    hiddenRetired,
  };
}
